/**
 * Derivation of the letter verification code (spec 12.3): the last 40 bits of the
 * letter's UUID (its last ten hex digits) read as eight Crockford base32 characters, so
 * the code is a function of `surat.id` and never stored twice. The sheet prints it as
 * "7K3M-Q2ZP"; the API and the public page look it up as stored (no hyphen). The reading
 * side (normalising what a person types) lives in ./verifikasi.
 *
 * Why the tail: a UUID v7 starts with its creation time in milliseconds and ends in random
 * bits. The spec's first draft took the first 40 bits, which made a code the issue time at
 * 256 ms resolution: guessable from the day a letter was issued (about 340,000 candidates)
 * against a lookup that needs no login, and shared by two letters issued in the same
 * quarter second. The last 40 bits are random, so a code cannot be predicted and a
 * collision needs about a million letters to become likely.
 *
 * Dependency-free and platform-neutral: the worker, the sheet and the API share it.
 */
import { formatKodeVerifikasi, isKodeVerifikasi } from './verifikasi'

/** Crockford's alphabet: digits, then letters without I, L, O and U. */
export const CROCKFORD_ALFABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'

const HEX_32 = /^[0-9a-f]{32}$/i

/**
 * The eight-character code of a letter id. Throws on anything that is not a UUID-shaped
 * string, because a code derived from garbage would print on paper.
 */
export function kodeVerifikasiDariId(id: string): string {
  const polos = id.replace(/-/g, '')
  if (!HEX_32.test(polos)) throw new Error(`kodeVerifikasiDariId: bukan UUID (${id})`)
  const hex = polos.slice(-10)
  // 40 bits fit a double exactly; read five bits at a time from the top.
  const n = Number.parseInt(hex, 16)
  let out = ''
  for (let i = 7; i >= 0; i--) {
    out += CROCKFORD_ALFABET[Math.floor(n / 2 ** (5 * i)) % 32]
  }
  return out
}

/** The code as it is stored and looked up, or null when the id is not a UUID. */
export function kodeVerifikasiAtauNull(id: string): string | null {
  try {
    const kode = kodeVerifikasiDariId(id)
    return isKodeVerifikasi(kode) ? kode : null
  } catch {
    return null
  }
}

/**
 * The URL the printed QR encodes: the public check of section 13 at `/periksa?kode=`
 * with the code in its printed form. `origin` comes from configuration (the app's own
 * origin or `VITE_PUBLIC_ORIGIN`), never from this module.
 */
export function urlVerifikasi(origin: string, kode: string): string {
  const base = origin.replace(/\/+$/, '')
  return `${base}/periksa?kode=${formatKodeVerifikasi(kode)}`
}
